// src/views/VocabChallengeView.jsx
import React, { useMemo, useEffect, useState } from 'react'; // ← añade useState
import { ArrowLeft } from 'lucide-react';
import { useVocabChallenge } from '../hooks/useVocabChallenge';
import VocabTimer from '../components/vocab/VocabTimer';
import VocabResultsScreen from '../components/vocab/VocabResultsScreen';
import { playSound } from '../utils/sounds';

const shuffleAndSlice = (arr, max) => {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, max);
};

const CHALLENGE_WORD_LIMIT = 12;

export default function VocabChallengeView({ set, onExit, onSaveXP, userProgress, game }) {
  const activeSkin   = game?.activeSkin || userProgress?.currentSkin || 'bribark';
  const isLegendary  = game?.isLegendary || false;

  // ✅ retryCount dispara nuevo shuffle en cada intento
  const [retryCount, setRetryCount] = useState(0);

  const words = useMemo(
    () => shuffleAndSlice(set?.words || [], CHALLENGE_WORD_LIMIT),
    [retryCount] // ← nuevo orden en cada retry
  );

  const XP_PER_WORD = 15;

  const {
    currentWord,
    options,
    timeLeft,
    score,
    results,
    phase,
    lastCorrect,
    cardIdx,
    totalWords,
    SECONDS_PER_WORD,
    onAnswer,
    reset, // ← del hook
  } = useVocabChallenge(words);

  useEffect(() => {
    if (phase === 'finished') {
      onSaveXP?.(score * XP_PER_WORD);
    }
  }, [phase]);

  // ✅ Baraja nuevas palabras Y resetea el hook
  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    reset();
  };

  if (phase === 'finished') {
    return (
      <VocabResultsScreen
        results={results}
        score={score}
        total={totalWords}
        xpGained={score * XP_PER_WORD}
        onExit={onExit}
        onRetry={handleRetry}
        activeSkin={activeSkin}
        isLegendary={isLegendary}
      />
    );
  }

  return (
    <div className="h-screen max-h-screen overflow-hidden bg-gradient-to-br from-indigo-50 to-purple-50 flex flex-col p-4 gap-4">

      {/* HEADER */}
      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={onExit}
          className="p-3 rounded-full bg-white border-2 border-slate-100 text-slate-400 hover:text-indigo-600 transition-all shadow-sm active:scale-95"
        >
          <ArrowLeft size={20} strokeWidth={2.5} />
        </button>

        <div className="flex-1 bg-white rounded-2xl px-4 py-2 border-2 border-indigo-50 shadow-sm">
          <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Desafío</p>
          <p className="text-sm font-bold text-slate-700 truncate">{set?.title}</p>
        </div>

        <div className="bg-white rounded-2xl px-4 py-2 border-2 border-indigo-50 shadow-sm text-center shrink-0">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Palabra</p>
          <p className="text-sm font-bold text-slate-700">{cardIdx + 1}/{totalWords}</p>
        </div>
      </div>

      {/* TIMER */}
      <VocabTimer timeLeft={timeLeft} total={SECONDS_PER_WORD} />

      {/* TARJETA */}
      <div className={`bg-white rounded-3xl shadow-xl p-5 text-center border-2 transition-all duration-300
        ${lastCorrect === true  ? 'border-emerald-400 shadow-emerald-100' : ''}
        ${lastCorrect === false ? 'border-red-400 shadow-red-100'         : ''}
        ${lastCorrect === null  ? 'border-indigo-50'                      : ''}
      `}>
        {lastCorrect === true  && <p className="text-emerald-500 font-black text-sm mb-2">✅ ¡Correcto!</p>}
        {lastCorrect === false && <p className="text-red-500 font-black text-sm mb-2">❌ ¡Tiempo!</p>}

        <p className="text-xs font-black text-indigo-400 uppercase tracking-widest mb-3">
          ¿Qué significa?
        </p>
        <h2 className="text-2xl font-black text-slate-800 leading-tight">
          {currentWord?.en}
        </h2>
        {currentWord?.ipa && (
          <span className="text-xs font-mono text-slate-400 mt-2 block">
            {currentWord.ipa}
          </span>
        )}
      </div>

      {/* OPCIONES */}
      <div className="grid grid-cols-2 gap-3 w-full">
        {options.map((opt, i) => {
          const isCorrectOpt = opt.es === currentWord?.es;
          const optStyle = phase === 'feedback'
            ? isCorrectOpt
              ? 'border-emerald-400 bg-emerald-50 text-emerald-700 scale-[1.02]'
              : 'border-slate-100 bg-slate-50 text-slate-400'
            : 'border-slate-100 bg-white text-slate-700 hover:border-indigo-300 hover:shadow-md active:scale-95';

          return (
            <button
              key={i}
              onClick={() => {
                if (phase !== 'playing') return;
                playSound(opt.es === currentWord?.es ? 'correct' : 'wrong');
                onAnswer(opt.es);
              }}
              disabled={phase !== 'playing'}
              className={`rounded-2xl py-4 px-3 font-bold text-sm shadow-sm border-2 transition-all duration-200 ${optStyle}`}
            >
              {opt.es}
            </button>
          );
        })}
      </div>

      {/* PUNTUACIÓN */}
      <div className="text-center">
        <span className="text-xs font-black text-slate-400 uppercase tracking-widest">
          ✅ {score} correctas
        </span>
      </div>

    </div>
  );
}
